/**
 * بلوك «النبذة التعريفيّة» (الثاني في الهوم): العنوان + النصّ + بطاقة الملاحظة
 * المائلة + الصورة التحريريّة + زرّ CTA — كلّها قابلة للتعديل من /admin/home-about.
 * يُخزَّن في app_settings تحت key="home_about_block".
 * أيّ حقل فارغ → fallback إلى النصّ/الصورة الافتراضيّة في الكود.
 */
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { TEXT_POSITIONS } from "@/lib/text-position";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { assertAdmin } from "./adminAuth.server";
import { homeAboutKeyForSlot, normalizeHomeAboutBlock, type HomeAboutSlot } from "./homeAboutBlock.server";

export type { HomeAboutSlot };

export type HomeAboutBlock = {
  enabled: boolean;
  eyebrowAr: string;
  eyebrowEn: string;
  titleAr: string;
  titleEn: string;
  bodyAr: string;
  bodyEn: string;
  noteAr: string;
  noteEn: string;
  ctaLabelAr: string;
  ctaLabelEn: string;
  ctaHref: string;
  imageUrl: string;
  /** وضع الصورة: كولاج صغير (افتراضي) أو صورة كاملة كخلفية للبلوك */
  imageMode: "collage" | "full";
  /** لون خلفيّة البلوك (hex) — بديل الصورة عندما تكون فارغة */
  bgColor: string;
  /** ترتيب النصّ داخل البلوك (٩ مواقع كنظام الهيرو) */
  textPosition: import("@/lib/text-position").TextPosition;
};

export const getHomeAboutBlock = createServerFn({ method: "GET" })
  .inputValidator((d: unknown) =>
    z
      .object({
        slot: z.enum(["primary", "secondary", "tertiary"]).default("primary"),
      })
      .parse(d ?? {}),
  )
  .handler(async ({ data }) => {
    try {
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      const { data: row, error } = await supabaseAdmin
        .from("app_settings")
        .select("value")
        .eq("key", homeAboutKeyForSlot(data.slot))
        .maybeSingle();
      if (error) console.error("[getHomeAboutBlock]", data.slot, error);
      return { block: normalizeHomeAboutBlock(row?.value ?? null) };
    } catch (err) {
      // قراءة عامّة للهوم: فشل عابر لا يجب أن يُسقط الصفحة.
      console.error("[getHomeAboutBlock]", data.slot, err);
      return { block: { ...normalizeHomeAboutBlock(null), enabled: false } };
    }
  });

export const setHomeAboutBlock = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z
      .object({
        slot: z.enum(["primary", "secondary", "tertiary"]).default("primary"),
        enabled: z.boolean(),
        eyebrowAr: z.string().max(120),
        eyebrowEn: z.string().max(120),
        titleAr: z.string().max(240),
        titleEn: z.string().max(240),
        bodyAr: z.string().max(4000),
        bodyEn: z.string().max(4000),
        noteAr: z.string().max(400),
        noteEn: z.string().max(400),
        ctaLabelAr: z.string().max(80),
        ctaLabelEn: z.string().max(80),
        ctaHref: z.string().max(500),
        imageUrl: z.string().max(800),
        imageMode: z.enum(["collage", "full"]).default("collage"),
        bgColor: z.string().max(20).optional(),
        textPosition: z.enum(TEXT_POSITIONS).default("center"),
      })
      .parse(d),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: existingRow } = await supabaseAdmin
      .from("app_settings")
      .select("value")
      .eq("key", homeAboutKeyForSlot(data.slot))
      .maybeSingle();
    const existing =
      existingRow?.value && typeof existingRow.value === "object"
        ? (existingRow.value as Record<string, unknown>)
        : {};
    const value = normalizeHomeAboutBlock({ ...existing, ...data });
    const { error } = await supabaseAdmin.from("app_settings").upsert(
      {
        key: homeAboutKeyForSlot(data.slot),
        value,
        updated_by: context.userId,
        updated_at: new Date().toISOString(),
      },
      { onConflict: "key" },
    );
    if (error) throw new Error(error.message);
    return { ok: true, block: value };
  });
